import { Link, Navigate, useParams } from "react-router-dom";
import { ArrowLeft, ArrowRight, Check, MapPin, MessageCircle } from "lucide-react";
import { Reveal } from "../components/Reveal";
import { SEO } from "../components/SEO";
import { ProjectCard } from "../components/ProjectCard";
import { PHONE_DISPLAY, PHONE_LINK, WHATSAPP_LINK, projects } from "../data/site";

export default function ProjectDetail() {
  const { slug } = useParams();
  const project = projects.find((p) => p.slug === slug);
  if (!project) return <Navigate to="/projects" replace />;

  const others = projects.filter((p) => p.slug !== slug).slice(0, 2);

  return (
    <main data-testid={`project-detail-${project.slug}`}>
      <SEO
        title={`${project.name}, ${project.location} — Parsn Group`}
        description={`${project.blurb} ${project.config}. MahaRERA: ${project.rera}.`}
        image={project.hero}
        path={`/projects/${project.slug}`}
      />
      <section className="relative pt-28 sm:pt-40 pb-14 sm:pb-20 bg-navy-deep text-white overflow-hidden">
        <div className="absolute inset-0">
          <img src={project.hero} alt="" aria-hidden="true" className="w-full h-full object-cover opacity-25" />
          <div className="absolute inset-0 bg-gradient-to-t from-navy-deep via-navy-deep/60 to-navy-deep/40" />
        </div>
        <div className="relative mx-auto max-w-7xl px-5 sm:px-8">
          <Reveal>
            <Link to="/projects" data-testid="back-to-projects-link" className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.2em] text-white/60 hover:text-white transition-colors duration-300 mb-8">
              <ArrowLeft size={14} /> All projects
            </Link>
            <p className="text-xs font-semibold uppercase tracking-[0.3em] text-brand mb-4">{project.tag} · {project.status}</p>
            <h1 className="font-serif text-4xl sm:text-6xl tracking-tight leading-[1.05]">{project.name}</h1>
            <p className="mt-5 flex items-center gap-2 text-white/70 text-base">
              <MapPin size={16} className="text-brand" /> {project.location}
            </p>
          </Reveal>
        </div>
      </section>

      <section className="bg-white py-16 sm:py-24">
        <div className="mx-auto max-w-7xl px-5 sm:px-8 grid lg:grid-cols-12 gap-12 items-start">
          <div className="lg:col-span-7">
            <Reveal>
              <p className="text-lg sm:text-xl text-gray-700 leading-relaxed max-w-2xl">{project.blurb}</p>
            </Reveal>
            {project.units.length > 0 && (
              <Reveal className="mt-12">
                <h2 className="overline-label mb-6">Residences</h2>
                <div className="grid sm:grid-cols-2 gap-px bg-gray-200 border border-gray-200">
                  {project.units.map((u) => (
                    <div key={u.type} className="bg-white p-6 sm:p-8">
                      <p className="font-serif text-2xl text-navy tracking-tight">{u.type}</p>
                      <p className="mt-2 text-sm text-gray-500">{u.area}</p>
                    </div>
                  ))}
                </div>
              </Reveal>
            )}
            {project.amenities.length > 0 && (
              <Reveal className="mt-12">
                <h2 className="overline-label mb-6">Amenities</h2>
                <ul className="grid sm:grid-cols-2 gap-4">
                  {project.amenities.map((a) => (
                    <li key={a} className="flex items-center gap-3 text-sm text-gray-700">
                      <span className="w-6 h-6 shrink-0 bg-navy/5 text-brand flex items-center justify-center"><Check size={14} /></span>
                      {a}
                    </li>
                  ))}
                </ul>
              </Reveal>
            )}
            {project.specs && project.specs.length > 0 && (
              <Reveal className="mt-12">
                <h2 className="overline-label mb-6">Specifications</h2>
                <ul className="grid sm:grid-cols-2 gap-4">
                  {project.specs.map((s) => (
                    <li key={s} className="flex items-center gap-3 text-sm text-gray-700">
                      <span className="w-1.5 h-1.5 shrink-0 bg-brand rotate-45" aria-hidden="true" />
                      {s}
                    </li>
                  ))}
                </ul>
              </Reveal>
            )}
            {project.connectivity && project.connectivity.length > 0 && (
              <Reveal className="mt-12">
                <h2 className="overline-label mb-6">Connectivity</h2>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-px bg-gray-200 border border-gray-200">
                  {project.connectivity.map((c) => (
                    <div key={c} className="bg-white px-4 py-5 flex items-center gap-2.5">
                      <MapPin size={13} className="text-brand shrink-0" />
                      <span className="text-xs sm:text-sm text-gray-700">{c}</span>
                    </div>
                  ))}
                </div>
              </Reveal>
            )}
            <Reveal className="mt-14">
              <h2 className="overline-label mb-6">Gallery</h2>
              <div className="grid grid-cols-2 gap-4">
                {project.gallery.map((g, i) => {
                  const item = typeof g === "string" ? { src: g, label: null } : g;
                  return (
                    <figure key={item.src} className={`group ${i === 0 && project.gallery.length > 1 ? "col-span-2" : i === 0 ? "col-span-2" : ""}`}>
                      <div className={`relative overflow-hidden border border-gray-200 ${i === 0 && project.gallery.length > 1 ? "aspect-[16/9]" : i === 0 ? "aspect-[4/5] sm:aspect-[3/2]" : "aspect-[4/3]"}`}>
                        <img src={item.src} alt={item.label || project.name} loading="lazy" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                        {item.label && (
                          <figcaption className="hidden sm:block absolute bottom-3 left-3 text-[10px] font-semibold uppercase tracking-[0.15em] bg-white/90 backdrop-blur px-3 py-1.5 text-navy">
                            {item.label}
                          </figcaption>
                        )}
                      </div>
                      {item.label && (
                        <figcaption className="sm:hidden mt-2 text-[11px] font-semibold uppercase tracking-[0.15em] text-gray-500">
                          {item.label}
                        </figcaption>
                      )}
                    </figure>
                  );
                })}
              </div>
            </Reveal>
            {project.plans && project.plans.length > 0 && (
              <Reveal className="mt-14">
                <h2 className="overline-label mb-6">Unit plans</h2>
                <div className="grid sm:grid-cols-2 gap-4">
                  {project.plans.map((p) => (
                    <figure key={p.src} className="group">
                      <div className="relative overflow-hidden border border-gray-200 bg-paper">
                        <img src={p.src} alt={p.label} loading="lazy" className="w-full object-contain transition-transform duration-700 group-hover:scale-[1.02]" />
                        <figcaption className="hidden sm:block absolute bottom-3 left-3 text-[10px] font-semibold uppercase tracking-[0.15em] bg-white/90 backdrop-blur px-3 py-1.5 text-navy">
                          {p.label}
                        </figcaption>
                      </div>
                      <figcaption className="sm:hidden mt-2 text-[11px] font-semibold uppercase tracking-[0.15em] text-gray-500">
                        {p.label}
                      </figcaption>
                    </figure>
                  ))}
                </div>
                <p className="mt-4 text-xs text-gray-400">Indicative layouts. Refer to the MahaRERA-registered plans for exact dimensions.</p>
              </Reveal>
            )}
          </div>

          <aside className="lg:col-span-5 lg:sticky lg:top-28">
            <Reveal className="border border-gray-200 bg-paper p-7 sm:p-9">
              <p className="overline-label mb-6">At a glance</p>
              <dl className="flex flex-col divide-y divide-gray-200">
                {[
                  ["Location", project.location],
                  ...(project.address ? [["Site address", project.address]] : []),
                  ["Configuration", project.config],
                  ["MahaRERA", project.rera],
                  ["Status", project.status],
                  ...(project.developer ? [["Developed by", project.developer]] : []),
                ].map(([k, v]) => (
                  <div key={k} className="py-4 flex flex-col gap-1">
                    <dt className="text-[11px] uppercase tracking-[0.2em] text-gray-400">{k}</dt>
                    <dd className="text-sm font-medium text-ink">{v}</dd>
                  </div>
                ))}
              </dl>
              <Link
                to={`/contact?project=${project.slug}`}
                data-testid="project-enquire-button"
                className="mt-6 flex items-center justify-center gap-2 bg-brand text-white text-sm font-semibold px-6 py-4 transition-[transform,background-color] duration-300 hover:-translate-y-0.5 hover:bg-[#e0671a]"
              >
                {project.cta || `Enquire about ${project.name.replace("Parsn ", "")}`} <ArrowRight size={16} />
              </Link>
              <a
                href={WHATSAPP_LINK}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="project-whatsapp-button"
                className="mt-3 flex items-center justify-center gap-2 border border-gray-300 text-navy text-sm font-semibold px-6 py-4 transition-colors duration-300 hover:border-navy hover:bg-navy hover:text-white"
              >
                <MessageCircle size={16} /> Chat on WhatsApp
              </a>
              <a
                href={PHONE_LINK}
                data-testid="project-call-button"
                className="mt-3 flex items-center justify-center gap-2 border border-gray-300 text-navy text-sm font-semibold px-6 py-4 transition-colors duration-300 hover:border-navy hover:bg-navy hover:text-white"
              >
                Call {PHONE_DISPLAY}
              </a>
            </Reveal>
          </aside>
        </div>
      </section>

      <section className="bg-paper border-t border-gray-200 py-16 sm:py-24">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <Reveal className="mb-10">
            <h2 className="font-serif text-2xl sm:text-4xl text-ink tracking-tight">Also under construction</h2>
          </Reveal>
          <div className="grid sm:grid-cols-2 gap-6 sm:gap-8 items-start">
            {others.map((p, i) => (
              <ProjectCard key={p.slug} project={p} index={i} />
            ))}
          </div>
        </div>
      </section>
    </main>
  );
}
